import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTop from '@/components/feature/BackToTop';
import PageMeta from '@/components/base/PageMeta';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const comic: Comic | undefined = comics.find((c) => c.id === slug);
  const categorySlug = comic ? categories.find((cat) => cat.name === comic.category)?.slug : '';
  const related = comic
    ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4)
    : [];

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <PageMeta
          title="漫画未找到 - 抖漫网页版"
          description="抱歉，您要查看的漫画不存在或已下架。"
          canonicalUrl="https://douman.readdy.ai"
        />
        <Navbar />
        <main className="pt-16">
          <div className="py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${comic.title} - 免费在线阅读 | 抖漫网页版`}
        description={`${comic.title}漫画简介：${comic.description.slice(0, 120)}... 在抖漫网页版免费在线阅读${comic.title}全部章节，更新及时，体验流畅。作者：${comic.author}，类型：${comic.category}。`}
        keywords={`${comic.title},${comic.title}漫画,${comic.title}在线阅读,抖漫网页版,${comic.category}漫画,${comic.author}`}
        canonicalUrl={`https://douman.readdy.ai/article/${comic.id}`}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${comic.title} - 免费在线阅读`,
          description: comic.description,
          url: `https://douman.readdy.ai/article/${comic.id}`,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2020-01-01',
          dateModified: comic.updatedAt,
          publisher: {
            '@type': 'Organization',
            name: '抖漫网页版',
            url: 'https://douman.readdy.ai',
          },
          isPartOf: {
            '@type': 'WebSite',
            name: '抖漫网页版',
            url: 'https://douman.readdy.ai',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://douman.readdy.ai' },
              { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://douman.readdy.ai/category/${categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `https://douman.readdy.ai/article/${comic.id}` },
            ],
          },
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">{t('article.breadcrumb_home')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors">
                {t('article.breadcrumb_category')}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full lg:w-[280px]">
                <div className="sticky top-20">
                  <div className="rounded-xl overflow-hidden aspect-[3/4] mb-4">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 抖漫网页版免费在线阅读`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/article/${comic.id}`}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      {t('article.read_first')}
                    </Link>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="text-sm text-foreground-600">
                    {t('article.author')}：{comic.author}
                  </span>
                  <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">
                    {comic.status}
                  </span>
                  <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                    {comic.category}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  <div className="p-3 rounded-lg bg-background-100/70 text-center">
                    <div className="text-lg font-bold text-accent-500">{comic.rating}</div>
                    <div className="text-xs text-foreground-500">{t('article.rating')}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100/70 text-center">
                    <div className="text-lg font-bold text-primary-500">{(comic.views / 10000).toFixed(0)}万</div>
                    <div className="text-xs text-foreground-500">{t('article.views')}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100/70 text-center">
                    <div className="text-lg font-bold text-secondary-500">{comic.chapters}</div>
                    <div className="text-xs text-foreground-500">{t('article.chapters')}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100/70 text-center">
                    <div className="text-lg font-bold text-foreground-950">{comic.updatedAt}</div>
                    <div className="text-xs text-foreground-500">最近更新</div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">{t('article.tags')}</h2>
                  <div className="flex flex-wrap gap-2">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">{t('article.description')}</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div>
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">{t('article.latest_chapters')}</h2>
                  <div className="space-y-2">
                    {Array.from({ length: 5 }, (_, i) => comic.chapters - i).map((ch) => (
                      <a
                        key={ch}
                        href={`/article/${comic.id}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-100/70 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="text-sm text-foreground-950 font-medium">
                          第{ch}{t('home.chapter')}
                        </span>
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {comic.updatedAt}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {related.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  {t('article.related_comics')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      className="group cursor-pointer no-underline"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                        <img
                          src={r.cover}
                          alt={r.title}
                          title={`${r.title} - 相关推荐`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-semibold rounded-full">
                            {r.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                        {r.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{r.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}